/**
 * score-color.ts — semantic color utility for numeric KPI values.
 *
 * Returns a Tailwind text-color class based on how a value sits within
 * its range. Matches the green/amber/red pattern used by Salesforce and
 * the Wavefunction reference screenshots.
 *
 * Thresholds (applied as percentage of max):
 *   ≥ 70%  → emerald  (good)
 *   40–69% → amber    (warning)
 *   < 40%  → red      (poor)
 *
 * Usage:
 *   <p className={scoreColor(3.4, 5)}>3.4 / 5</p>
 *   <p className={scoreColor(8.2, 10)}>8.2</p>
 *
 * The function is intentionally dependency-free (no React import) so it
 * can be used in both TSX components and plain TS utilities.
 */

export type ScoreColorClass =
  | "text-emerald-600 dark:text-emerald-400"
  | "text-amber-500 dark:text-amber-400"
  | "text-red-500 dark:text-red-400";

/**
 * Returns a Tailwind text-color class for `value` relative to `max`.
 *
 * @param value  The numeric value to evaluate.
 * @param max    The maximum possible value (defaults to 100).
 */
export function scoreColor(value: number, max = 100): ScoreColorClass {
  const pct = max === 0 ? 0 : (value / max) * 100;
  if (pct >= 70) return "text-emerald-600 dark:text-emerald-400";
  if (pct >= 40) return "text-amber-500 dark:text-amber-400";
  return "text-red-500 dark:text-red-400";
}

/**
 * Returns a Tailwind background + text badge class pair for use on pill badges.
 *
 * @param value  The numeric value to evaluate.
 * @param max    The maximum possible value (defaults to 100).
 */
export function scoreBadgeColor(
  value: number,
  max = 100,
): string {
  const pct = max === 0 ? 0 : (value / max) * 100;
  if (pct >= 70)
    return "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300";
  if (pct >= 40)
    return "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300";
  return "bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300";
}
